import runtimeConfig from './runtimeConfig';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Route, Routes, useNavigate, Navigate, Outlet, useOutletContext as useRouterOutletContext, useLocation, useParams } from 'react-router-dom';
import Sidebar from "./common-components/Sidebar.jsx";
import Dashboard from "./common-components/Dashboard.jsx";
import Topbar from "./common-components/Topbar.jsx";
import Login from "./common-components/Login.jsx";
import { GoogleOAuthProvider } from '@react-oauth/google';
import ProtectedRoute from "./routes/ProtectedRoute.jsx";
import AuthenticatedLoginRedirect from "./routes/AuthenticatedLoginRedirect.jsx";
import FormsList from "./dashboard-components/forms/FormsList.jsx";
import FormBuilder from "./dashboard-components/forms/FormBuilder.jsx";
import PublicFormViewer from "./dashboard-components/forms/PublicFormViewer.jsx";
import McpPromptManagement from "./dashboard-components/dashboard/developer_hub/McpPromptManagement.jsx";
import McpOAuthConsent from "./dashboard-components/mcp_oauth/McpOAuthConsent.jsx";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastProvider } from './context/ToastContext';
import server from "./api/server";
import RemindersLayout from "./dashboard-components/reminders/RemindersLayout.jsx";
// Utility function
function getCookie(name) {
  function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
  var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
  return match ? match[1] : null;
}

// Menu to path mapping
const MENU_TO_PATH = {
  'Home': '/home',
  'Agent Feedback Overall': '/agent-feedback/overview',
  'Agent Feedback Agent Wise': '/agent-feedback/agent-wise',
  'Agent Feedback Team Wise': '/agent-feedback/team-wise',
  'Agent Feedback Agent Training': '/agent-feedback/training',
  'Agent Feedback Reports': '/agent-feedback/reports',
  'Customer Insights': '/customer-insights',
  'Custom Reports': '/custom-reports',
  'Conversations': '/conversations',
  'Concern Query': '/concerns-queries',
  'Customers': '/customers',
  'Events': '/events',
  'Apps': '/apps',
  'Data Sources': '/data-sources',
  'Project Settings': '/project-settings',
  'Billing': '/billing',
  'Organization Settings': '/organization-settings',
  'Call ID': '/conversations',
  "Customer ID": '/customers',
  'Event ID': '/events',
  "Report ID": '/agent-feedback/reports',
  "Training ID": '/agent-feedback/training',
  'Configuration': '/developer-hub/configuration',
  'Documentation': '/developer-hub/documentation',

  'VA Home': '/home',
  'VA Agents': '/agents', 
  'VA Widgets': '/widgets',
  'VA Conversations': '/conversations',
  'VA Customers': '/customers',
  'VA Campaigns': '/campaigns',
  'VA Knowledge Base': '/knowledge-base',
  'VA Call ID': '/conversations',
  'VA Customer ID': '/customers',
  'VA Campaign ID': '/campaigns',
  'VA Phone Number': '/phone-numbers',
  'VA Agent ID': '/agents'
};

// Path to menu mapping
const PATH_TO_MENU = Object.entries(MENU_TO_PATH).reduce((acc, [menu, path]) => {
  acc[path] = menu;
  return acc;
}, {});

const decodePathSegment = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

// Main App component
const App = () => {
  const [user, setUser] = useState(JSON.parse(getCookie('dashboard_login_user_creds')) ? JSON.parse(getCookie('dashboard_login_user_creds')) : null);
  
  // Get initial clientId and serviceId from URL if available (with platform prefix)
  const pathname = window.location.pathname;
  const pathParts = pathname.split('/').filter(Boolean);
  const hasPlatformPrefix = pathParts.length >= 3 && (pathParts[0] === 'analytics' || pathParts[0] === 'voice-agents');
  const clientFromPath = hasPlatformPrefix ? decodePathSegment(pathParts[1]) : null;
  const serviceFromPath = hasPlatformPrefix ? decodePathSegment(pathParts[2]) : null;
  const firstRunRef = useRef(true);
  
  const [selectedOptions, setSelectedOption] = useState(serviceFromPath ? serviceFromPath : (user?.service_id ? user.service_id : 'DEFAULT'));
  const [selectedClientId, setSelectedClientId] = useState(clientFromPath ? clientFromPath : user?.client_id);
  // Resolve effective identity from either state or cookie (helps right after login)
  const cookieUser = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
  const effectiveUser = user || cookieUser;
  const effectiveClientId = selectedClientId || effectiveUser?.client_id;
  const effectiveServiceId = selectedOptions || effectiveUser?.service_id || 'DEFAULT';
  
  const [activeDashboardNum, setActiveDashboardNum] = useState(() => {
    if(!user || !user.dashboardData || !user.dashboardData[0] || !user.dashboardData[0].data || !user.dashboardData[0].data[0]){
      return null;
    }
    return user?.dashboardData[0]?.data[0]?.dashboard_link;
  });

  const handleClientIdChange = useCallback((e) => {
    setSelectedClientId(e)
  }, [])

  // Service change handler
  const handleChangeService = useCallback((e) => {
    setSelectedOption(e);
  }, []);
  useEffect(()=> {
    if(user && !selectedClientId){
      setSelectedClientId(user.client_id)
    }
  }, [user])
  useEffect(() => {
        if ( !user) return;
        if (!firstRunRef.current) return;   // guard against loops
       firstRunRef.current = false;
       if(user && !selectedClientId){
        setSelectedClientId(user.client_id)
       }
    }, [ user, selectedClientId, selectedOptions]);

  return (
    <GoogleOAuthProvider clientId={runtimeConfig.REACT_APP_GOOGLE_CLIENT_ID}>
      <ToastProvider>
      <Routes>
        {/* Public entrypoint for embedded token links */}
        <Route path="/embedded/*" element={<EmbeddedAuth />} />
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            effectiveUser && effectiveClientId && effectiveServiceId
              ? <AuthenticatedLoginRedirect fallback={`/analytics/${effectiveClientId}/${effectiveServiceId}/home`} />
              : <Login />
          }
        />
        
        {/* Public Forms Route - No authentication required */}
        <Route path="/public/forms/:formId" element={<PublicFormViewer />} />
        
        {/* Direct Forms Routes - Protected but without clientId/serviceId in URL */}
        <Route path="/reminders" element={<ProtectedRoute><RemindersLayout /></ProtectedRoute>} />
        <Route path="/forms" element={<ProtectedRoute><DirectFormsLayout /></ProtectedRoute>} />
        <Route path="/forms/create" element={<ProtectedRoute><DirectFormsLayout /></ProtectedRoute>} />
        <Route path="/forms/:formId/edit" element={<ProtectedRoute><DirectFormsLayout /></ProtectedRoute>} />
        <Route path="/forms/:formId/view" element={<ProtectedRoute><DirectFormsLayout /></ProtectedRoute>} />
        <Route path="/mcp-prompt-management" element={<ProtectedRoute><DirectMcpPromptManagementLayout /></ProtectedRoute>} />
        <Route path="/mcp-prompt-management/create" element={<ProtectedRoute><DirectMcpPromptManagementLayout /></ProtectedRoute>} />
        <Route path="/mcp-prompt-management/:promptId/edit" element={<ProtectedRoute><DirectMcpPromptManagementLayout /></ProtectedRoute>} />
        <Route path="/mcp/oauth/consent" element={<ProtectedRoute><McpOAuthConsent /></ProtectedRoute>} />
        
        {/* Protected layout route */}
        <Route element={<ProtectedRoute><MainLayout selectedOptions={selectedOptions} setSelectedOptions={handleChangeService} activeDashboardNum={activeDashboardNum} setActiveDashboardNum={setActiveDashboardNum} selectedClientId={selectedClientId} setSelectedClientId={handleClientIdChange} /></ProtectedRoute>}>
          {/* Analytics routes */}
          <Route path="/analytics/:clientId/:serviceId/home" element={<DashboardView menu="Home" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/overview" element={<DashboardView menu="Agent Feedback Overall" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/agent-wise" element={<DashboardView menu="Agent Feedback Agent Wise" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/team-wise" element={<DashboardView menu="Agent Feedback Team Wise" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/training" element={<DashboardView menu="Agent Feedback Agent Training" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/reports" element={<DashboardView menu="Agent Feedback Reports" />} />
          <Route path="/analytics/:clientId/:serviceId/customer-insights" element={<DashboardView menu="Customer Insights" />} />
          <Route path="/analytics/:clientId/:serviceId/custom-reports" element={<DashboardView menu="Custom Reports" />} />
          <Route path="/analytics/:clientId/:serviceId/custom-reports/:reportId" element={<DashboardView menu="Custom Reports" />} />
          <Route path="/analytics/:clientId/:serviceId/conversations" element={<DashboardView menu="Conversations" />} />
          <Route path="/analytics/:clientId/:serviceId/concerns-queries" element={<DashboardView menu="Concern Query" />} />
          <Route path="/analytics/:clientId/:serviceId/customers" element={<DashboardView menu="Customers" />} />
          <Route path="/analytics/:clientId/:serviceId/events" element={<DashboardView menu="Events" />} />
          <Route path="/analytics/:clientId/:serviceId/apps" element={<DashboardView menu="Apps" />} />
          <Route path="/analytics/:clientId/:serviceId/data-sources" element={<DashboardView menu="Data Sources" />} />
          <Route path="/analytics/:clientId/:serviceId/project-settings" element={<DashboardView menu="Project Settings" />} />
          <Route path="/analytics/:clientId/:serviceId/billing" element={<DashboardView menu="Billing" />} />
          <Route path="/analytics/:clientId/:serviceId/organization-settings" element={<DashboardView menu="Organization Settings" />} />
          <Route path="/analytics/:clientId/:serviceId/developer-hub/configuration" element={<DashboardView menu="Configuration" />} />
          <Route path="/analytics/:clientId/:serviceId/developer-hub/documentation" element={<DashboardView menu="Documentation" />} />

          {/* Voice Agents routes */}
          <Route path="/voice-agents/:clientId/:serviceId/home" element={<DashboardView menu="VA Home" />} />
          <Route path="/voice-agents/:clientId/:serviceId/agents" element={<DashboardView menu="VA Agents" />} />
          <Route path="/voice-agents/:clientId/:serviceId/widgets" element={<DashboardView menu="VA Widgets" />} />
          <Route path="/voice-agents/:clientId/:serviceId/widgets/:widgetId" element={<DashboardView menu="VA Widgets" />} />
          <Route path="/voice-agents/:clientId/:serviceId/conversations" element={<DashboardView menu="VA Conversations" />} />
          <Route path="/voice-agents/:clientId/:serviceId/customers" element={<DashboardView menu="VA Customers" />} />
          <Route path="/voice-agents/:clientId/:serviceId/campaigns" element={<DashboardView menu="VA Campaigns" />} />
          <Route path="/voice-agents/:clientId/:serviceId/knowledge-base" element={<DashboardView menu="VA Knowledge Base" />} />
          <Route path="/voice-agents/:clientId/:serviceId/phone-numbers" element={<DashboardView menu="VA Phone Number" />} />
          
          {/* Voice Agents detail routes */}
          <Route path="/voice-agents/:clientId/:serviceId/conversations/:callId" element={<DashboardView menu="VA Call ID" />} />
          <Route path="/voice-agents/:clientId/:serviceId/customers/:customerId" element={<DashboardView menu="VA Customer ID" />} />
          <Route path="/voice-agents/:clientId/:serviceId/campaigns/:campaignId" element={<DashboardView menu="VA Campaign ID" />} />
          <Route path="/voice-agents/:clientId/:serviceId/agents/:agentId" element={<DashboardView menu="VA Agent ID" />} />
          
          {/* Detail routes */}
          <Route path="/analytics/:clientId/:serviceId/conversations/:callId" element={<DashboardView menu="Call ID" />} />
          <Route path="/analytics/:clientId/:serviceId/customers/:customerId" element={<DashboardView menu="Customer ID" />} />
          <Route path="/analytics/:clientId/:serviceId/events/:eventReferenceId" element={<DashboardView menu="Event ID" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/reports/:reportId" element={<DashboardView menu="Report ID" />} />
          <Route path="/analytics/:clientId/:serviceId/agent-feedback/training/:trainingId" element={<DashboardView menu="Training ID" />} />
        </Route>
        
        {/* Redirects */}
        <Route path="/home" element={ user && effectiveClientId && effectiveServiceId ? <Navigate to={`/analytics/${effectiveClientId}/${effectiveServiceId}/home`} replace /> : <Login /> } />
        <Route path="*" element={ user && effectiveClientId && effectiveServiceId ? <Navigate to={`/analytics/${effectiveClientId}/${effectiveServiceId}/home`} replace /> : <Login /> }  />
      </Routes>
      </ToastProvider>
    </GoogleOAuthProvider>
  );
};

// Main Layout Component - shared across protected routes
const MainLayout = ({ selectedOptions, setSelectedOptions, activeDashboardNum, setActiveDashboardNum, selectedClientId, setSelectedClientId }) => {
  const { clientId, serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();  
  // Use a ref to prevent navigation loops
  const isNavigatingRef = useRef(false);
  const initialRenderRef = useRef(true);
  
  // Determine the current menu based on the route
  const [selectedMenu, setSelectedMenu] = useState(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const isVoice = segments[0] === 'voice-agents';
    const afterBase = '/' + segments.slice(isVoice || segments[0] === 'analytics' ? 3 : 2).join('/');
    const entries = Object.entries(MENU_TO_PATH).filter(([menu]) =>
      isVoice ? menu.startsWith('VA ') : !menu.startsWith('VA ')
    );
    for (const [menu, menuPath] of entries) {
      if (afterBase.startsWith(menuPath)) {
        return menu;
      }
    }
    return isVoice ? 'VA Home' : 'Home';
  });
  
  const [subSelectedMenu, setSubSelectedMenu] = useState(null);
  const [openContact, setOpenContact] = useState(false);
  
  // Effect to sync URL params with state on initial render
  useEffect(() => {
    if (initialRenderRef.current && clientId && serviceId) {
      if (clientId !== selectedClientId) {
        setSelectedClientId(clientId);
      }
      if (serviceId !== selectedOptions) {
        setSelectedOptions(serviceId);
      }
      initialRenderRef.current = false;
    }
  }, [clientId, serviceId, selectedClientId, selectedOptions, setSelectedClientId, setSelectedOptions]);
  
  // Clean up the navigation ref when unmounting
  useEffect(() => {
    return () => {
      isNavigatingRef.current = false;
      initialRenderRef.current = false;
    };
  }, []);
  
  // Keep URL in sync with selected client/service
  useEffect(() => {
    // Skip on initial render to prevent overriding URL params
    if (!initialRenderRef.current && (clientId !== selectedClientId || serviceId !== selectedOptions)) {
      const segs = location.pathname.split('/').filter(Boolean);
      const hasPlatform = segs[0] === 'analytics' || segs[0] === 'voice-agents';
      const platform = hasPlatform ? segs[0] : 'analytics';
      const currentPath = segs.slice(hasPlatform ? 3 : 2).join('/'); // slice after /platform/clientId/serviceId
      
      // Don't interfere with navigation if we're already on a detail page with encoded parameters
      // This prevents the sync effect from breaking URLs with special characters
      const isDetailPage =
        currentPath.startsWith('conversations/') ||
        currentPath.startsWith('customers/') ||
        currentPath.startsWith('reports/') ||
        currentPath.startsWith('custom-reports/') ||
        currentPath.startsWith('agent-feedback/reports/') ||
        currentPath.startsWith('agent-feedback/training/') ||
        currentPath.startsWith('campaigns/');
      if (isDetailPage) {
        return; // Let the detail pages handle their own navigation
      }
      
      if (!isNavigatingRef.current) {
        isNavigatingRef.current = true;
        // Preserve query parameters when syncing URL
        const fullUrl = `/${platform}/${selectedClientId}/${selectedOptions}/${currentPath}${location.search}`;
        navigate(fullUrl, { replace: true });
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      }
    }
  }, [selectedClientId, selectedOptions, clientId, serviceId, location.pathname, navigate, initialRenderRef]);
  
  // Handle menu selection from sidebar - WITH NAVIGATION
  const handleMenuSelect = useCallback((menu) => {
    if (isNavigatingRef.current) return;
  
    const relPath = MENU_TO_PATH[menu];
    if (!relPath) return;
  
    // Preserve query parameters when navigating between pages
    const currentSearch = location.search;
    const platform = menu.startsWith('VA ') ? 'voice-agents' : 'analytics';
    const fullPath = `/${platform}/${selectedClientId}/${selectedOptions}${relPath}`;
    const fullPathWithQuery = `${fullPath}${currentSearch}`;
    
    if (location.pathname === fullPath) {
      setSelectedMenu(menu);
      return;
    }
  
    isNavigatingRef.current = true;
    setSelectedMenu(menu);
    navigate(fullPathWithQuery);
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 100);
  }, [navigate, location.pathname, location.search, selectedClientId, selectedOptions]);
  
  
  // Handle contact toggling
  const handleOpenContact = useCallback((val) => {
    setOpenContact(val === true);
  }, []);

  const isEmbed = new URLSearchParams(location.search).get('embed') === '1';
  const isVoiceAgentLayout = location.pathname.startsWith('/voice-agents/');

  return (
    <div style={{height: isVoiceAgentLayout ? "100dvh" : undefined, overflow: isVoiceAgentLayout || window.innerWidth <= 768 ? "hidden" : "", display: isEmbed ? "block" : "flex"}}>
      {!isEmbed && (
      <Sidebar
        setSelectedMenu={handleMenuSelect} // This now triggers navigation
        selectedMenu={selectedMenu}
        setOpenContact={handleOpenContact}
        setSubSelectedMenu={setSubSelectedMenu}
        selectedOption={selectedOptions}
        setActiveDashboardNum={setActiveDashboardNum}
        selectedClientId={selectedClientId}
        setSelectedClientId={setSelectedClientId}
      />
      )}
      <div className="flex" style={{flexDirection: "column", width: isEmbed ? "100%" : "83%"}}>
        {!isEmbed && (
        <Topbar setSelectedOptions={setSelectedOptions} selectedClientId={selectedClientId} selectedServiceId={serviceId} />
        )}
        <Outlet context={{
          selectedMenu,
          subSelectedMenu,
          openContact,
          setOpenContact: handleOpenContact,
          selectedOptions,
          dashNum: activeDashboardNum,
          setSelectedMenu: handleMenuSelect,
          selectedClientId
        }} />
      </div>
      <ToastContainer/>
    </div>
  );
};

// Dashboard View Component - renders the Dashboard with menu props
const DashboardView = ({ menu }) => {
  // Use the real useOutletContext from react-router-dom
  const {
    subSelectedMenu,
    openContact,
    setOpenContact,
    selectedOptions,
    dashNum,
    selectedMenu,
    setSelectedMenu,
    selectedClientId
  } = useRouterOutletContext();
  
  return (
    <Dashboard
      selectedMenu={menu} // Use the menu from route props
      subSelectedMenu={subSelectedMenu}
      openContact={openContact}
      setOpenContact={setOpenContact}
      selectedOptions={selectedOptions}
      dashNum={dashNum}
      setSelectedMenu={setSelectedMenu}
      selectedClientId={selectedClientId}
    />
  );
};

// Direct Forms Layout Component - handles /forms routes without clientId/serviceId
const DirectFormsLayout = () => {
  const location = useLocation();
  const { formId } = useParams();
  const user = JSON.parse(getCookie('dashboard_login_user_creds'));
  
  // Get clientId and serviceId from user data
  const clientId = user?.client_id;
  const serviceId = user?.service_id || 'DEFAULT';
  
  // Create a wrapper component that provides the missing params
  const FormsWrapper = ({ children }) => {
    return React.cloneElement(children, { 
      directClientId: clientId, 
      directServiceId: serviceId,
      directFormId: formId 
    });
  };
  
  // Determine which component to render based on the route
  const renderComponent = () => {
    const path = location.pathname;
    
    if (path === '/forms') {
      return <FormsWrapper><FormsList /></FormsWrapper>;
    } else if (path === '/forms/create') {
      return <FormsWrapper><FormBuilder /></FormsWrapper>;
    } else if (path.includes('/edit')) {
      return <FormsWrapper><FormBuilder /></FormsWrapper>;
    } else if (path.includes('/view')) {
      return <FormsWrapper><FormsList /></FormsWrapper>; // or a specific form view component
    }
    
    return <FormsWrapper><FormsList /></FormsWrapper>; // default
  };
  
  return (
    <div style={{ 
      minHeight: "100vh", 
      backgroundColor: "#f8fafc",
      display: "flex",
      justifyContent: "center",
      padding: "20px"
    }}>
      <div style={{ 
        width: "100%", 
        maxWidth: "80%", 
        minWidth: "800px" // Ensure minimum usable width
      }}>
        {renderComponent()}
      </div>
      <ToastContainer/>
    </div>
  );
};

const DirectMcpPromptManagementLayout = () => {
  const location = useLocation();
  const { promptId } = useParams();
  const user = JSON.parse(getCookie('dashboard_login_user_creds'));
  const clientId = user?.client_id;
  const serviceId = user?.service_id || 'DEFAULT';
  const mode = location.pathname.endsWith('/create') ? 'create' : promptId ? 'edit' : 'list';

  if (user?.role !== 'superadmin') {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc" }}>
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "24px", color: "#111827" }}>
          MCP prompt management is available only to superadmin users.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#f8fafc",
      display: "flex",
      justifyContent: "center",
      padding: "20px"
    }}>
      <div style={{
        width: "100%",
        maxWidth: "90%",
        minWidth: "800px"
      }}>
        <McpPromptManagement
          clientID={clientId}
          serviceID={serviceId}
          directPromptId={promptId}
          directMode={mode}
        />
      </div>
      <ToastContainer/>
    </div>
  );
};

const EmbeddedAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Full path after "/embedded/"
    const remainingPath = location.pathname.replace(/^\/embedded\/?/, '');
    if (!remainingPath) {
      setLoading(false);
      return;
    }

    // Preserve existing query params (should already have token)
    const params = new URLSearchParams(location.search);
    params.set('embed', '1');

    // Redirect path
    const redirectTo = `/analytics/${remainingPath}${params.toString() ? '?' + params.toString() : ''}`;
    if (getCookie('dashboard_login_user_creds')) {
      navigate(redirectTo, { replace: true });
      window.location.reload();
      return;
    }
    let userData = null;
        
        userData = {
            email: params.get('email'),
            client_id: params.get('client_id'),
            service_id: params.get('service_id'),
            loginTime: new Date().getTime(),
            role: null,
            name: null,
            'session_id': params.get('session_id')
          };
        // Save user session data in cookies (expire in 12 hours)
        const currentDate = new Date();

        // Set the expiry date to 30 days from now
        currentDate.setTime(currentDate.getTime() + (30 * 24 * 60 * 60 * 1000));

        // Format the expiry date in UTC format
        const expires = `expires=${currentDate.toUTCString()}`;

        // Set the cookie with the expiration time
        document.cookie = `dashboard_login_user_creds=${JSON.stringify(userData)}; ${expires}; path=/; Secure; SameSite=None`;

    navigate(redirectTo, { replace: true });
    window.location.reload();
  }, [location.pathname, location.search, navigate]);

  return (
    <div className="flex items-center justify-center h-screen">
      {loading ? (
        <div className="flex justify-center items-center" style={{ height: '88%', flexDirection: 'column' }}>
          <div className="loader"></div>
          <div style={{ textAlign: 'center' }}>Loading <br /> Please Wait</div>
        </div>
      ) : (
        <div>Link Expired</div>
      )}
    </div>
  );
};

export default App;
